import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listCarriedOver,
  carryOver,
  updateGoal,
  type CarriedOverTask,
  type GoalStatus,
  bulkUpdateTasks,
  getTaskOverview,
  taskOverviewKey,
} from '../api';
import { useAccountDay } from '../accountDay';
import { shiftDay } from '../timezones';
import { dueLook, latePileSentence } from '../taskVocab';
import { useAssignees } from '../useAssignees';

/**
 * The late pile, counted once — and yesterday's plan, asked about once.
 *
 * **One band, where Today had two.** This band asked about stale plans and `LateWork` beneath it
 * (Phase 99, `01a08099-fcb1`) counted `byStatus.late`. Late is a passed deadline *or* a passed plan
 * on a task with no deadline, so every plan-only stale task was in both — four personas read
 * *"3 things … planned as far back as 93 days ago"* directly above *"3 things are late"* about the
 * same three rows, and *"24 things"* above *"36 things are late"* with the 24 inside the 36
 * (`01a15a31-14a5`). Two sentences, one pile, and nothing to say that the second contained the first.
 *
 * So the count is now the server's, split by which clock made each row late — `lateByDeadline` and
 * `lateByPlan` off `/api/tasks/overview`, which sum to `byStatus.late` and are the same two figures
 * the Tasks summary prints. The headline is the whole pile once; *See them* is the press for the
 * whole of it, and the carry-over answers below are for the plan half, which is the half a press
 * here can actually settle. A deadline is usually somebody else's, and its triage lives on Tasks.
 *
 * Why the late half joined this band rather than the other way round: this one already carried the
 * verbs, the Review list and the paused-goal rows (`01a08094-6fc8`), and `LateWork`'s own argument
 * — that it had to be separate because this band returned null with no stale plans — is answered by
 * rendering when *either* half has something, which is the shape below.
 *
 * The left edge follows `dueLook`'s hue rule: red only where a real deadline has passed, amber
 * where the pile is plans alone. Red for a stale plan is the defect `taskVocab.ts` was fixed for.
 *
 * The day plan has always let overdue work into the shortlist — `rankCandidate` ranks a stale plan
 * second, under only the deadlines that are due or passed — but it arrived indistinguishable from
 * work that was never planned at all. On the account this was
 * built against, six tasks dated yesterday sat in a pile of ten with nothing marking them, while
 * today's plan read as empty. Nothing anywhere said "you decided to do these and did not".
 *
 * That is what quietly kills planning as a practice: if a plan evaporates overnight without
 * comment, the lesson is that writing one down changes nothing, and the rational response to that
 * lesson is to stop writing them.
 *
 * Two things this deliberately is not:
 *
 * - **Not automatic.** A task silently re-dated every night for four months is not a plan, it is
 *   a renewing accusation, and the only way to stop it is to confront the thing you have been
 *   avoiding — exactly what you will not do. Moving costs one press so that *not* moving is
 *   equally cheap.
 * - **Not dismissible.** There is no "later" button, because dismissing without deciding is the
 *   behaviour that produces a hundred stale dates. Take it today, or take the date off. Both are
 *   answers; neither is a deferral.
 */

type Press = 'today' | 'week' | 'clear';

const PRESS_DID: Record<Press, (n: number) => string> = {
  today: (n) => `Moved ${n} to today`,
  week: (n) => `Moved ${n} to next week`,
  clear: (n) => `Took the planned date off ${n}`,
};

/**
 * What a bulk press did, from the server's count — including the press it refused.
 *
 * The short case names the shortfall rather than the figure alone: "Moved 0 to today" reads as a
 * press with nothing to act on, when the band had just listed the rows it was sent for. The likely
 * cause is named as likely, because the band offered the press from a role it believed the viewer
 * held, and a role changing under an open page is the one way that belief goes wrong.
 */
function readbackSentence(what: Press, sent: number, moved: number): string {
  if (moved >= sent) return `${PRESS_DID[what](moved)}.`;
  const refused = sent - moved;
  return `${PRESS_DID[what](moved)} — the server refused ${refused} of ${sent}; your role on ${refused === 1 ? 'its project' : 'their projects'} may have changed.`;
}

/** "yesterday", "3 days ago" — the age is the part that should feel different. */
const ago = (days: number) => (days === 1 ? 'yesterday' : `${days} days ago`);

/*
  Three decisions, not one "not live".

  `goal.live` is the predicate and the server owns it — but paused, done and archived are three
  different things the user did, and a row that called them all "set aside" would be the same
  defect wearing a predicate, which is the argument `GoalDetail`'s banner already makes. The verb
  on the button follows the word: you resume something you paused and reopen something you
  finished, and both are the one call that makes the goal live again.
*/
const PARKED_WORD: Record<Exclude<GoalStatus, 'active'>, { said: string; verb: string }> = {
  paused: { said: 'is paused', verb: 'Resume goal' },
  done: { said: 'is done', verb: 'Reopen goal' },
  archived: { said: 'is archived', verb: 'Reopen goal' },
};

function Row({
  task,
  date,
  mayDate,
  refusal,
}: {
  task: CarriedOverTask;
  date: string;
  /** Whether the viewer's role on the task's project lets them set or clear its plan date. */
  mayDate: boolean;
  /** Why not, in `roleReason`'s words — null while the membership loads. */
  refusal: string | null;
}) {
  const queryClient = useQueryClient();
  /*
    `dueLook` is the single place that decides how a deadline reads, so the panel and the pill
    cannot drift into two vocabularies. `scheduledDate` is deliberately not passed: its fallback
    branch would print "planned …" a second time, which is the sentence already to its left.

    Both of its clocks are the account's now. It was handed `todayIso()` — the *browser's* day —
    while every other number on this band came off `date`, the day the view is pointed at; and the
    zone is what turns a stored `dueAt` instant into a day at all. Two clocks on one row: "planned
    3 days ago" counted in the account's calendar beside "2d late" counted in the laptop's.
  */
  const { timezone } = useAccountDay();
  const due = dueLook({ dueAt: task.dueAt }, date, timezone);
  const act = useMutation({
    mutationFn: (unschedule: boolean) => carryOver(date, { taskIds: [task.id], unschedule }),
    onSuccess: () => queryClient.invalidateQueries(),
  });
  /*
    `active` is excluded here rather than trusted away: the field is only ever set for a goal that
    is not live, but narrowing on the status is what lets `PARKED_WORD` be exhaustive, so a fifth
    goal status fails the typecheck here instead of printing `undefined` at somebody.
  */
  const parked =
    task.parkedGoal === null || task.parkedGoal.status === 'active' ? null : task.parkedGoal.status;
  const revive = useMutation({
    mutationFn: () => updateGoal(task.parkedGoal!.id, { status: 'active' }),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  return (
    <li className="carried-row" data-testid="carried-task" data-task-title={task.title}>
      <span className="carried-title">{task.title}</span>
      {/*
        Both clocks, in the pill's own words.

        This said "planned 3 days ago" while the shortlist 700px below said "12d late" about the
        same task: one counting from the promise, the other from the deadline. Both defensible
        alone; together on one screen the panel built for batch triage was the one showing the
        reassuring number. Neither is dropped — a task can be freshly re-planned and long overdue,
        and that combination is exactly the one worth seeing — but the deadline is named as a
        deadline so the two numbers stop looking like disagreement.
      */}
      <span className="subtext">
        planned {ago(task.daysAgo)}
        {due && <> · {due.text}</>}
        {/*
          Why this row is here and nowhere else on the screen.

          Without it the band and the shortlist 700px below simply disagreed — three things listed
          here, two of them findable there — and the reader had no way to learn that the missing
          one was missing on purpose. Named goal, not "a paused goal": the account has nine, and
          which one was set aside is the fact that makes the Resume beside it a decision rather
          than a guess.

          Set in the subtext's own muted voice rather than a pill: this is the row explaining
          itself, not a third thing telling you off on a screen a persona round once counted 25
          negative statements on. The band also has a height budget with a test behind it.
        */}
        {parked && (
          <>
            {' '}
            · its goal “{task.parkedGoal!.title}” {PARKED_WORD[parked].said}
          </>
        )}
      </span>
      <span className="row gap">
        {/*
          For work under a goal the user has stood down, the press that changes something.

          "Today" was drawn here and did nothing a reader could see: the shortlist beside this
          band drops parked work, Goals today drops the goal, so dating the task to today only
          removed the last row that mentioned it. The aspirational planner's account had its
          **only** in-progress task in exactly that position — offered a button whose whole effect
          was to make it invisible.

          Replaced, not disabled, and not hidden: a greyed-out affordance with nothing saying why
          is the complaint the paused habit card was fixed for one round ago, and an empty gap
          leaves no answer to "so how do I get this back". Resume is the one act that makes the
          row's promise keepable again — after it the goal is live, the task returns to the
          shortlist, and "Today" means something.
        */}
        {parked ? (
          <button
            type="button"
            data-testid="resume-parked-goal"
            disabled={revive.isPending}
            onClick={() => revive.mutate()}
            title={`Make “${task.parkedGoal!.title}” live again`}
          >
            {PARKED_WORD[parked].verb}
          </button>
        ) : (
          mayDate && (
            <button type="button" disabled={act.isPending} onClick={() => act.mutate(false)}>
              Today
            </button>
          )
        )}
        {/*
          Not a delete: the task stays, only the promise about when comes off.

          Named "Clear date" rather than the "not today" it started as, because that read as a
          near-duplicate of the button beside it — one word apart visually, and announced as
          "Today" and "not today" by a screen reader, which is two ways to mis-hit the wrong
          answer to a question about avoidance.
        */}
        {mayDate && (
          <button
            type="button"
            className="link"
            disabled={act.isPending}
            onClick={() => act.mutate(true)}
            title="Keep the task, drop the date"
          >
            Clear date
          </button>
        )}
      </span>
      {/*
        A row the viewer's role may not re-date says so, in place of the two presses.

        Both were drawn for every row, and for a reporter both answered 200 `{ moved: 0 }` — the
        server refuses a plan date below `assign` without a 403, so the band re-fetched unchanged
        and the press read as a broken app. Replaced rather than greyed, for the reason the parked
        row above gives: a disabled button with nothing beside it is a quieter silence.
        `roleReason` is the sentence every other door prints, so the band cannot word the role
        differently from the task panel. Its own line, because at 375 the presses share a narrow
        right-hand column that a sentence would crush the title against.
      */}
      {!mayDate && refusal && (
        <span className="subtext carried-refusal" data-testid="carried-refusal">
          {refusal}
        </span>
      )}
    </li>
  );
}

export function CarriedOver({ date, onSeeLate }: { date: string; onSeeLate: () => void }) {
  const queryClient = useQueryClient();
  const carried = useQuery({
    queryKey: ['carry-over', date],
    queryFn: () => listCarriedOver(date),
  });
  /*
    The late figures, read rather than counted here — the Tasks summary shows the same three, and
    two screens deriving "how much is late" from the same rows is how they come to disagree. `mine`,
    said rather than defaulted, because Today has no scope control; the key is the helper's, so this
    shares one fetch with `WorkElsewhere`. Silent while loading and on failure: the plan half can
    still render, and an error about a count would be louder than the count.
  */
  const overview = useQuery({
    queryKey: taskOverviewKey(),
    queryFn: () => getTaskOverview(),
  });
  /*
    What the last bulk press did, in the server's figure.

    Both mutations re-fetched on success and printed nothing, throwing `moved` away. That was
    harmless while every offered press landed, and it is not now that the offer is decided from a
    role cache a minute stale by design (`roles.ts`): a role changed under an open page still
    produces a press the server answers with 200 `{ moved: 0 }`. Without this line that press and
    a broken app look the same, which is the silence `roles.ts`'s header was written against.
  */
  const [readback, setReadback] = useState<{ what: Press; sent: number; moved: number } | null>(
    null,
  );
  const all = useMutation({
    mutationFn: (v: { unschedule: boolean; taskIds: string[] }) =>
      carryOver(date, { unschedule: v.unschedule, taskIds: v.taskIds }),
    onMutate: () => setReadback(null),
    onSuccess: (r, v) => {
      setReadback({
        what: v.unschedule ? 'clear' : 'today',
        sent: v.taskIds.length,
        moved: r.moved,
      });
      return queryClient.invalidateQueries();
    },
  });
  /*
    A third way out of the pile: later.

    "Move to today" and "Clear dates" are the two the banner has always offered, and both are a
    decision about *now* — take it on, or admit it has no date. Two personas asked for the third:
    the pile is often work that is real but not this week, and pushing it out is neither doing it
    nor abandoning it. `bulkUpdateTasks` already takes a scheduled date, so this needs no endpoint
    of its own.
  */
  const push = useMutation({
    /*
      Counted from the day the band is reviewing, not from the browser's clock.

      It assembled its own date out of `getFullYear`/`getMonth`/`getDate` — the laptop's day — while
      "Move to today" beside it goes through `carryOver(date)`, the account's. Two answers to the
      same pile, counted from two different calendars, and the band only renders for the account's
      today anyway, so `date` is the day it means and there is no second clock left to disagree.
    */
    mutationFn: (v: { days: number; ids: string[] }) =>
      bulkUpdateTasks(v.ids, { scheduledDate: shiftDay(date, v.days) }),
    onMutate: () => setReadback(null),
    onSuccess: (r, v) => {
      setReadback({ what: 'week', sent: v.ids.length, moved: r.updated });
      return queryClient.invalidateQueries();
    },
  });
  /*
    One line, and the list behind it.

    Measured on the owner's account this band was 482 pixels tall with sixteen buttons — seven
    tasks each carrying "Today" and "Clear date", plus two bulk actions — and it sat *above* the
    day. So the screen opened with half a viewport of things you had already failed to do before
    showing you anything you could do now.

    The prompt is still there, because it should be: work quietly rotting with a stale date on it
    is worth knowing about. It just does not need to be the largest thing on the screen. Both bulk
    answers stay on the line; the per-task decisions move behind "Review", which is where you go
    when the bulk answer is not the right one.
  */
  const [reviewing, setReviewing] = useState(false);

  const tasks = carried.data?.tasks ?? [];
  /*
    The viewer's role per project, from the membership cache `useAssignees` already shares with
    Tasks' *Take it on*. The rule is the server's (`FIELD_LEVEL.scheduledDate`, `assign`); this
    only mirrors the ladder to decide what to offer — see `roles.ts`.
  */
  const { can, why } = useAssignees(tasks);
  const late = overview.data?.byStatus.late ?? 0;
  const byDeadline = overview.data?.lateByDeadline ?? 0;
  const byPlan = overview.data?.lateByPlan ?? 0;
  /*
    Either half is enough to draw the band. Stale plans alone (each with a deadline still ahead)
    are a question with nothing late; missed deadlines alone are late with nothing to ask — which
    is the shape `LateWork` was split off to reach, and why this does not return on `tasks` alone.
  */
  if (tasks.length === 0 && late === 0) return null;

  /*
    The two bulk answers that set a date act on the rows whose own button offers one.

    "Move to today" over a task under a paused goal is the inert press the row's "Today" was, done
    to several rows at once and without the row there to explain it — it would date the work to a
    day that shows it nowhere and take the last mention of it off the screen. So the parked rows
    keep their place and their Resume, and if every leftover is parked the two date buttons are
    not drawn at all: "Clear dates" is still a real answer for them, and Review is where the
    per-row one lives.

    `carryOver`'s own meaning is untouched. An MCP client asking to move everything to a day is
    making a general statement about dates; this is one band deciding which rows its buttons
    speak for.
  */
  /*
    And all three act only on rows whose project role lets the viewer set a plan date.

    A reporter holding a stale plan was listed here — it is theirs, and the late count counts it —
    and offered *Move to today*, *Next week* and *Clear the planned date*, each of which the server
    answered with `{ moved: 0 }`. Since the clear press states a figure, it also promised more
    dates than it took off. So the presses send these ids and count these rows, and when none are
    left they are not drawn and the band says why once. Parked rows stay in `writable`: clearing
    a date is still a real answer for them, as the paragraph above says.
  */
  const writable = tasks.filter((t) => can(t.projectId, 'assign'));
  const datable = writable.filter((t) => t.parkedGoal === null);
  const refusalFor = (t: CarriedOverTask) => why(t.projectId, 'assign');
  const bandRefusal =
    writable.length === 0 ? (tasks.map(refusalFor).find((r) => r !== null) ?? null) : null;

  /*
    The oldest one sets the tone: a single leftover from yesterday is a normal Tuesday, and
    something from three weeks ago is a different conversation.

    Index zero, because the list arrives oldest-first — reading the last element said "going back
    to yesterday" above a row that read "planned 2 days ago", which was visible on the very first
    real account it ran against and invisible to the spec, since that only asserted the phrase
    appeared *somewhere* in the band and the rows were supplying it.
  */
  const oldest = tasks[0]?.daysAgo ?? 0;

  return (
    <div
      className={byDeadline > 0 ? 'carried-over has-missed-deadline' : 'carried-over'}
      data-testid="carried-over"
    >
      <p className="carried-head">
        <span>
          {/*
            The whole pile once, when any of it is late; otherwise the plans, which are then all
            this band is about. Never both counts as two headlines — that is the defect.
          */}
          <strong data-testid="late-count">
            {late > 0
              ? `${late} late`
              : `${tasks.length} ${tasks.length === 1 ? 'thing' : 'things'}`}
          </strong>{' '}
          {/*
            For the whole of it. The Tasks screen arrives aimed at `filters.late` — the predicate
            the headline counted — so it opens both halves, not only the plans Review lists.
          */}
          {late > 0 && (
            <>
              <button type="button" className="link" data-testid="see-late" onClick={onSeeLate}>
                See them
              </button>{' '}
            </>
          )}
          {/*
            Review belongs to the count, not to the run of answers.

            It was the fourth child of `.carried-actions`, drawn exactly like the two links beside
            it — measured on the deployed build at 375 with nine leftovers, "Next week", "Clear
            dates" and "Review" were each `border 0px`, transparent, `rgb(28, 28, 26)`, underlined,
            `padding 4px 0px`, and none carried a title. The run wrapped there, so its second line
            was "Clear dates" at top 271 and "Review" at top 271: one line reading as one offer,
            whose left half takes the planned date off every leftover and whose right half changes
            nothing at all.

            The other three presses answer the question the band asks; this one only opens the rows
            it is asking about, which is why it alone carries `aria-expanded`. Beside "9 things" is
            where its subject is, and it is also the only free place to put it: `.subtext` below is
            `display: block`, so anything after that block starts a third line, and a third line
            costs 23px of a band with 1px of its 140px budget left. On the count's line there are
            210 spare pixels at 375.
          */}
          {tasks.length > 0 && (
            <button
              type="button"
              className="link"
              aria-expanded={reviewing}
              onClick={() => setReviewing((v) => !v)}
            >
              {reviewing ? 'Hide' : 'Review'}
            </button>
          )}
          <span className="subtext" data-testid="late-split">
            {late > 0
              ? latePileSentence(byDeadline, byPlan, overview.data?.oldestLateDays ?? null)
              : `planned ${tasks.length === 1 ? ago(oldest) : `as far back as ${ago(oldest)}`}, still open.`}
          </span>
        </span>
        {tasks.length > 0 && (
          <span className="carried-actions">
            {datable.length > 0 && (
              <button
                type="button"
                disabled={all.isPending}
                onClick={() => all.mutate({ unschedule: false, taskIds: datable.map((t) => t.id) })}
              >
                Move to today
              </button>
            )}
            {datable.length > 0 && (
              <button
                type="button"
                className="link"
                disabled={push.isPending}
                onClick={() => push.mutate({ days: 7, ids: datable.map((t) => t.id) })}
              >
                {/*
              "Next week", not "Push to next week". This band has a height budget with a test
              behind it — it was once 482px tall with sixteen buttons — and the longer label
              wrapped the action row onto a second line at 375px, taking it to 145px against a
              140px guard. The verb is already carried by the row it sits in.
            */}
                Next week
              </button>
            )}
            {/*
            The one press here that takes something away, marked and counted.

            "Move to today" and "Next week" write a date; this one removes it, and unlike them it
            acts on parked leftovers too rather than only on the datable ones — so on the account this was
            measured against it was the only answer of the three whose reach was nine rows. It said
            "Clear dates", in the same bare underlined word as its two neighbours, and offered no
            number and no hover text. `danger` is the app's name for a control that removes
            something; the count is there because nine is the fact that makes this a decision.

            Not a confirm, deliberately, and not a disable: `01a03cd8-aa9a` argues an undo beats a
            confirmation because a confirmation is one more decision, and Phase 81 chose replacing
            a control over greying one out twice in one phase. What was wrong here was that the
            press did not say what it was; saying it is the fix, not a gate in front of it.

            Its figure is named as planned dates, not as a bare count (`01a15e6b-0027`). It said
            "Clear dates on 6" under "2 missed deadlines and 3 missed plans": the six are every
            stale plan, whatever its deadline, while the sentence's halves partition *late* — so a
            stale plan whose deadline has passed is a missed deadline there and one of the six here,
            and one whose deadline is still ahead is only here. "Missed plans" and "dates" read as
            one noun, and two figures for it on one line were the overlap this band was rebuilt to
            remove. The sentence stays a partition, because it is the Tasks summary's too; the
            button says what it removes. A wording judgement, not a measurement: a reader still
            taking "3 missed plans" against "6 planned dates" as a contradiction would move the
            bridge into the sentence instead, at the cost of a line in a 140px budget.

            The two presses beside it carry no figure, so they have no count to disagree with.
          */}
            {writable.length > 0 && (
              <button
                type="button"
                className="link danger"
                disabled={all.isPending}
                onClick={() => all.mutate({ unschedule: true, taskIds: writable.map((t) => t.id) })}
                title={
                  writable.length === 1
                    ? 'This task is planned for a day that has passed, whatever its deadline — keep the task, drop the date'
                    : `${writable.length} tasks are planned for a day that has passed, whatever their deadline — keep the tasks, drop the dates`
                }
              >
                {writable.length === 1
                  ? 'Clear the planned date'
                  : `Clear ${writable.length} planned dates`}
              </button>
            )}
            {bandRefusal && (
              <span className="subtext carried-refusal" data-testid="carried-refusal">
                {bandRefusal}
              </span>
            )}
          </span>
        )}
      </p>

      {readback && (
        <p className="subtext" role="status" data-testid="carried-readback">
          {readbackSentence(readback.what, readback.sent, readback.moved)}
        </p>
      )}

      {reviewing && (
        <ul className="carried-list">
          {tasks.map((task) => (
            <Row
              key={task.id}
              task={task}
              date={date}
              mayDate={writable.includes(task)}
              refusal={refusalFor(task)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
